import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { ChevronDown, Radar, Cpu, Waves } from 'lucide-react';
import Counter from './animations/Counter';
import { heroStats, submarineImages } from '../data/mock';

/**
 * Hero with scroll-driven zoom-out from a massive rotating gear
 * to a full submarine schematic where the gear is just a tiny mechanism part.
 */
export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });

  // Smooth spring for buttery scroll
  const sp = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.6 });

  // The giant gear shrinks dramatically as we scroll
  // Starts at 9x scale (way bigger than viewport), ends at 0.06x (tiny mechanism)
  const gearScale = useTransform(sp, [0, 1], [9, 0.06]);
  // Slight rotation in addition to CSS continuous spin
  const gearRotate = useTransform(sp, [0, 1], [0, 220]);

  // Submarine schematic: invisible at start, fades in as we zoom out enough
  const subOpacity = useTransform(sp, [0, 0.45, 0.7, 1], [0, 0, 0.85, 1]);
  const subScale = useTransform(sp, [0, 0.7, 1], [0.6, 0.85, 1]);

  // Hero text fades and rises away as user scrolls
  const textOpacity = useTransform(sp, [0, 0.18], [1, 0]);
  const textY = useTransform(sp, [0, 0.25], [0, -60]);

  // Stats panel comes in later
  const statsOpacity = useTransform(sp, [0.55, 0.78], [0, 1]);
  const statsY = useTransform(sp, [0.55, 0.78], [40, 0]);

  // Section indicator
  const phase = useTransform(sp, [0, 0.35, 0.7, 1], [
    'PHASE 01 / GEAR DETAIL',
    'PHASE 02 / ZOOM OUT',
    'PHASE 03 / SUB SECTION',
    'PHASE 04 / FULL VIEW',
  ]);

  // Progress bar
  const progressWidth = useTransform(sp, [0, 1], ['0%', '100%']);

  return (
    <section id="hero" ref={ref} className="relative" style={{ height: '320vh' }}>
      {/* Sticky stage holds animation in viewport for entire section duration */}
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-[#05080d]">
        {/* Background grids */}
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="absolute inset-0 bg-grid-fine opacity-30" />
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0, 80, 130, 0.35) 0%, transparent 70%)',
          }}
        />

        {/* THE GIANT GEAR — this is the centerpiece */}
        <motion.div
          style={{
            scale: gearScale,
            rotate: gearRotate,
          }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 will-change-transform"
        >
          {/* Continuous self-rotation layered on top of scroll-rotate via wrapper */}
          <div className="spin-slow">
            <DetailedGear />
          </div>
        </motion.div>

        {/* SUBMARINE SCHEMATIC — fades in once we've zoomed out enough */}
        <motion.div
          style={{ opacity: subOpacity, scale: subScale }}
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
        >
          <SubmarineSchematic />
        </motion.div>

        {/* Hero copy */}
        <motion.div
          style={{ opacity: textOpacity, y: textY }}
          className="absolute inset-0 z-10 flex items-center"
        >
          <div className="max-w-7xl w-full mx-auto px-6 md:px-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-px bg-cyan-400" />
              <span className="text-[10px] md:text-xs font-mono tracking-[0.4em] text-cyan-300">
                CLASS · UTKU-001 · RESTRICTED
              </span>
              <div className="w-2 h-2 bg-cyan-400 blink" />
            </div>

            <h1 className="font-display font-bold leading-[0.85] tracking-tight">
              <span className="block text-[15vw] md:text-[10vw] lg:text-[9rem] text-white text-glow">
                UTKU
              </span>
              <span className="block text-2xl md:text-4xl lg:text-5xl text-cyan-300/90 mt-2 font-light tracking-[0.15em]">
                DERİN SULARIN GARDIYANI
              </span>
            </h1>

            <p className="max-w-xl mt-8 text-slate-400 text-base md:text-lg leading-relaxed">
              Yeni nesil AIP tahrik sistemi, entegre savaş yönetimi ve son derece düşük akustik
              imzası ile UTKU sınıfı denizaltılar; mavi vatanın derinliklerinde sessiz caydırıcılık
              sağlar.
            </p>

            <div className="mt-8 flex items-center gap-3 text-cyan-400/60">
              <ChevronDown className="w-4 h-4 animate-bounce" />
              <span className="text-[10px] font-mono tracking-[0.3em]">
                MEKANİZMAYI KEŞFETMEK İÇİN KAYDIRIN
              </span>
            </div>
          </div>
        </motion.div>

        {/* Stats overlay — appears after we've revealed the submarine */}
        <motion.div
          style={{ opacity: statsOpacity, y: statsY }}
          className="absolute bottom-0 left-0 right-0 z-10 pb-8"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-cyan-500/15 border border-cyan-500/15 backdrop-blur-sm">
              {heroStats.map((s, i) => (
                <div
                  key={i}
                  className="relative bg-[#070b12]/80 p-4 md:p-5 group hover:bg-[#0a1320]/90 transition-colors"
                >
                  <span className="absolute top-2 right-3 text-[9px] font-mono text-cyan-400/40">
                    0{i + 1}
                  </span>
                  <div className="text-[9px] md:text-[10px] font-mono tracking-[0.25em] text-cyan-400/70 mb-2">
                    {s.label}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-2xl md:text-4xl font-bold text-white tabular-nums">
                      <Counter value={s.value} decimals={s.value.includes('.') ? 2 : 0} />
                    </span>
                    <span className="text-[10px] font-mono text-cyan-300/60">{s.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Side telemetry */}
        <div className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-10 flex-col items-center gap-3 text-cyan-400/50">
          <span className="text-[10px] font-mono rotate-180" style={{ writingMode: 'vertical-rl' }}>
            DEPTH — 0042 M / 0400 M MAX
          </span>
          <div className="w-px h-24 bg-gradient-to-b from-cyan-400/0 via-cyan-400 to-cyan-400/0" />
        </div>
        <div className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-10 flex-col items-center gap-3 text-cyan-400/50">
          <div className="w-px h-24 bg-gradient-to-b from-cyan-400/0 via-cyan-400 to-cyan-400/0" />
          <span className="text-[10px] font-mono" style={{ writingMode: 'vertical-rl' }}>
            BEARING — N 042°
          </span>
        </div>

        {/* Bottom status row + scroll progress */}
        <div className="absolute bottom-0 left-0 right-0 z-20 border-t border-cyan-500/15 bg-[#05080d]/80 backdrop-blur-sm">
          <motion.div className="h-px bg-cyan-400" style={{ width: progressWidth }} />
          <div className="flex items-center justify-between px-6 md:px-10 py-2.5 text-[10px] font-mono tracking-[0.25em] text-cyan-400/70">
            <div className="flex items-center gap-4 md:gap-6">
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-cyan-400 blink" />
                <motion.span>{phase}</motion.span>
              </span>
              <span className="hidden md:flex items-center gap-2">
                <Radar className="w-3 h-3" /> SONAR
              </span>
              <span className="hidden md:flex items-center gap-2">
                <Cpu className="w-3 h-3" /> CMS
              </span>
              <span className="hidden md:flex items-center gap-2">
                <Waves className="w-3 h-3" /> AIP
              </span>
            </div>
            <span className="hidden md:block text-cyan-300/80">UTKU · SSK CLASS · S-401</span>
          </div>
        </div>

        {/* Hidden submarine bg image to add depth (very subtle) */}
        <div
          className="absolute inset-0 opacity-10 mix-blend-screen pointer-events-none"
          style={{
            backgroundImage: `url(${submarineImages.hero})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'grayscale(0.7) contrast(1.1) brightness(0.5)',
          }}
        />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#05080d] to-transparent pointer-events-none" />
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------- */
/* DETAILED MECHANICAL GEAR — premium look like the reference video */
/* ----------------------------------------------------------------- */
function DetailedGear() {
  const teeth = 24;
  return (
    <svg
      width="600"
      height="600"
      viewBox="-300 -300 600 600"
      className="text-cyan-400"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <radialGradient id="metalGrad" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#1d3a52" />
          <stop offset="55%" stopColor="#0c1a26" />
          <stop offset="100%" stopColor="#050a10" />
        </radialGradient>
        <radialGradient id="hubGrad" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#234a68" />
          <stop offset="100%" stopColor="#091523" />
        </radialGradient>
        <linearGradient id="toothShine" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#2a5d80" />
          <stop offset="100%" stopColor="#0a1a28" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Outer faint halo */}
      <circle cx="0" cy="0" r="270" fill="none" stroke="rgba(0,212,255,0.12)" strokeWidth="0.6" strokeDasharray="2 6" />
      <circle cx="0" cy="0" r="255" fill="none" stroke="rgba(0,212,255,0.08)" strokeWidth="0.4" />

      {/* Teeth */}
      {Array.from({ length: teeth }).map((_, i) => {
        const angle = (i * 360) / teeth;
        return (
          <g key={i} transform={`rotate(${angle})`}>
            <path
              d="M -16 -250 L -14 -228 L 14 -228 L 16 -250 Z"
              fill="url(#toothShine)"
              stroke="#0a1a28"
              strokeWidth="0.6"
            />
            <line x1="-13" y1="-245" x2="13" y2="-245" stroke="#3a7aa0" strokeWidth="0.4" opacity="0.5" />
          </g>
        );
      })}

      {/* Outer rim */}
      <circle cx="0" cy="0" r="228" fill="url(#metalGrad)" stroke="#0a1a28" strokeWidth="1.5" />
      <circle cx="0" cy="0" r="225" fill="none" stroke="#1d4868" strokeWidth="0.8" />
      <circle cx="0" cy="0" r="218" fill="none" stroke="#0a1a28" strokeWidth="0.6" />

      {/* Decorative inner ring with bolts */}
      <circle cx="0" cy="0" r="200" fill="none" stroke="#152a40" strokeWidth="2" />
      {Array.from({ length: 16 }).map((_, i) => {
        const a = (i * 360) / 16;
        const x = Math.cos((a * Math.PI) / 180) * 200;
        const y = Math.sin((a * Math.PI) / 180) * 200;
        return (
          <g key={i}>
            <circle cx={x} cy={y} r="5" fill="#0a1a28" stroke="#2a5d80" strokeWidth="0.6" />
            <circle cx={x} cy={y} r="2.5" fill="#1a3a55" />
            <circle cx={x} cy={y} r="1" fill="#3a7aa0" />
          </g>
        );
      })}

      {/* Spoke ring */}
      <circle cx="0" cy="0" r="180" fill="none" stroke="#1d4868" strokeWidth="0.6" strokeDasharray="3 4" opacity="0.6" />

      {/* Big spokes (6) */}
      {[0, 60, 120, 180, 240, 300].map((a) => (
        <g key={a} transform={`rotate(${a})`}>
          <path
            d="M -14 -190 L -22 -60 L -10 -50 L 10 -50 L 22 -60 L 14 -190 Z"
            fill="url(#metalGrad)"
            stroke="#0a1a28"
            strokeWidth="0.8"
          />
          <line x1="0" y1="-185" x2="0" y2="-55" stroke="#2a5d80" strokeWidth="0.5" opacity="0.5" />
          {/* Spoke detail rivets */}
          <circle cx="0" cy="-160" r="2.5" fill="#1a3a55" stroke="#3a7aa0" strokeWidth="0.4" />
          <circle cx="0" cy="-120" r="2.5" fill="#1a3a55" stroke="#3a7aa0" strokeWidth="0.4" />
          <circle cx="0" cy="-80" r="2.5" fill="#1a3a55" stroke="#3a7aa0" strokeWidth="0.4" />
        </g>
      ))}

      {/* Hub assembly */}
      <circle cx="0" cy="0" r="60" fill="url(#hubGrad)" stroke="#1d4868" strokeWidth="1" />
      <circle cx="0" cy="0" r="58" fill="none" stroke="#2a5d80" strokeWidth="0.6" />
      <circle cx="0" cy="0" r="48" fill="none" stroke="#0a1a28" strokeWidth="2" />
      <circle cx="0" cy="0" r="42" fill="#091523" />
      {/* Hub bolts */}
      {[0, 60, 120, 180, 240, 300].map((a) => {
        const x = Math.cos((a * Math.PI) / 180) * 32;
        const y = Math.sin((a * Math.PI) / 180) * 32;
        return (
          <g key={a}>
            <circle cx={x} cy={y} r="3.5" fill="#0a1a28" stroke="#3a7aa0" strokeWidth="0.5" />
            <circle cx={x} cy={y} r="1.5" fill="#2a5d80" />
          </g>
        );
      })}
      {/* Center axis */}
      <circle cx="0" cy="0" r="14" fill="#0a1a28" stroke="#2a5d80" strokeWidth="1" />
      <circle cx="0" cy="0" r="10" fill="#152a40" />
      <circle cx="0" cy="0" r="4" fill="#3a7aa0" filter="url(#glow)" />

      {/* Cyan accent highlights */}
      <circle cx="0" cy="0" r="228" fill="none" stroke="rgba(0,212,255,0.08)" strokeWidth="2" />
      <path
        d="M 0 -228 A 228 228 0 0 1 200 -110"
        fill="none"
        stroke="rgba(0,212,255,0.18)"
        strokeWidth="1"
      />
    </svg>
  );
}

/* ----------------------------------------------------------------- */
/* SUBMARINE SCHEMATIC with the gear placement marker                */
/* ----------------------------------------------------------------- */
function SubmarineSchematic() {
  return (
    <svg
      viewBox="0 0 1400 500"
      className="w-[95%] max-w-[1400px] h-auto text-cyan-400"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id="hullGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="rgba(0,212,255,0.08)" />
          <stop offset="50%" stopColor="rgba(0,212,255,0.02)" />
          <stop offset="100%" stopColor="rgba(0,212,255,0.08)" />
        </linearGradient>
      </defs>

      {/* Side profile of submarine - simplified blueprint */}
      <g stroke="currentColor" strokeWidth="1" fill="url(#hullGrad)">
        {/* Main hull */}
        <path
          d="M 60 250
             Q 100 200, 200 195
             L 1100 195
             Q 1240 195, 1340 235
             Q 1350 250, 1340 265
             L 1180 280
             L 200 305
             Q 100 300, 60 250 Z"
        />
        {/* Sail / conning tower */}
        <path d="M 540 195 L 560 140 L 700 140 L 720 195 Z" />
        {/* Periscope */}
        <line x1="630" y1="140" x2="630" y2="95" strokeWidth="1.2" />
        <circle cx="630" cy="92" r="3" fill="currentColor" />
        <line x1="650" y1="140" x2="650" y2="105" strokeWidth="1" opacity="0.7" />
        {/* Rear stabilizers */}
        <path d="M 1200 195 L 1240 165 L 1260 195 Z" opacity="0.7" />
        <path d="M 1200 305 L 1240 335 L 1260 305 Z" opacity="0.7" />
        {/* Propeller hub */}
        <circle cx="1340" cy="250" r="14" fill="none" />
        <line x1="1340" y1="220" x2="1340" y2="280" />
        <line x1="1310" y1="250" x2="1370" y2="250" />
        {/* Bow torpedo tubes */}
        <circle cx="100" cy="232" r="6" fill="none" />
        <circle cx="100" cy="252" r="6" fill="none" />
        <circle cx="100" cy="270" r="6" fill="none" />
      </g>

      {/* Internal compartment dividers */}
      <g stroke="currentColor" strokeWidth="0.6" strokeDasharray="3 4" opacity="0.5">
        <line x1="280" y1="200" x2="280" y2="300" />
        <line x1="450" y1="200" x2="450" y2="300" />
        <line x1="600" y1="200" x2="600" y2="300" />
        <line x1="780" y1="200" x2="780" y2="300" />
        <line x1="950" y1="200" x2="950" y2="300" />
        <line x1="1100" y1="200" x2="1100" y2="300" />
      </g>

      {/* The gear's location marker - center of engine room */}
      <g>
        <circle cx="870" cy="250" r="22" fill="none" stroke="#00d4ff" strokeWidth="1.5" />
        <circle cx="870" cy="250" r="34" fill="none" stroke="#00d4ff" strokeWidth="0.6" strokeDasharray="2 3" opacity="0.7">
          <animate attributeName="r" values="34;42;34" dur="3s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.7;0.2;0.7" dur="3s" repeatCount="indefinite" />
        </circle>
        {/* Leader line */}
        <line x1="870" y1="228" x2="870" y2="80" stroke="#00d4ff" strokeWidth="0.8" />
        <line x1="870" y1="80" x2="970" y2="80" stroke="#00d4ff" strokeWidth="0.8" />
        <text x="980" y="76" fill="#00d4ff" fontSize="11" fontFamily="JetBrains Mono, monospace">
          PWR-07 · ANA TAHRİK DİŞLİSİ
        </text>
        <text x="980" y="92" fill="#00d4ff" fontSize="9" fontFamily="JetBrains Mono, monospace" opacity="0.6">
          MAIN PROPULSION GEAR
        </text>
      </g>

      {/* Other markers */}
      <g fill="#00d4ff" fontFamily="JetBrains Mono, monospace" fontSize="10">
        <circle cx="630" cy="170" r="3" />
        <text x="640" y="165">A1 · OPTRONİK MAST</text>
        <circle cx="220" cy="250" r="3" />
        <text x="230" y="245">B2 · TORPİDO BÖLMESİ</text>
        <circle cx="1180" cy="250" r="3" />
        <text x="1100" y="335">C3 · MOTOR DAİRESİ</text>
      </g>

      {/* Measurement scale */}
      <g stroke="currentColor" strokeWidth="0.5" opacity="0.6" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="currentColor">
        <line x1="60" y1="370" x2="1340" y2="370" />
        <line x1="60" y1="365" x2="60" y2="375" />
        <line x1="1340" y1="365" x2="1340" y2="375" />
        <text x="680" y="388" textAnchor="middle">68.34 m</text>
      </g>

      {/* Drawing label */}
      <g fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#00d4ff">
        <text x="60" y="430" opacity="0.7">DWG-UTKU-001 · SIDE PROFILE</text>
        <text x="60" y="446" opacity="0.5">REV. 04 / 2026 · SCALE 1:200</text>
        <text x="1340" y="430" textAnchor="end" opacity="0.7">CLASSIFIED · INTERNAL USE</text>
      </g>
    </svg>
  );
}
